import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { manchiApi } from "@/lib/manchiApi";
import { Button } from "@/components/ui/button";
import { Loader2, Newspaper, RefreshCw, Globe2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DOT_COLORS = [
  "bg-pink-400", "bg-rose-400", "bg-fuchsia-400", "bg-purple-400",
  "bg-orange-400", "bg-amber-400",
];

export default function ManchiDaily() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const pollRef = useRef(null);

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await manchiApi.get("/daily-brief");
      setBrief(data.brief);
      return data.brief;
    } catch { return null; }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const generate = async () => {
    setBuilding(true);
    try {
      await manchiApi.post("/daily-brief/generate");
      toast.success("Gathering today's news… this takes ~30s");
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        ticks += 1;
        const b = await load(true);
        if ((b && b.sections && b.sections.length) || ticks >= 8) {
          clearInterval(pollRef.current);
          setBuilding(false);
        }
      }, 6000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate brief");
      setBuilding(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="manchi-daily">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-rose-400 flex items-center gap-2">
            <Newspaper className="w-4 h-4" /> today&apos;s world
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-rose-900 mt-1">The Daily Brief.</h1>
          <p className="text-rose-500 text-sm mt-2">
            Major headlines across the topics you care about — not just economics. Edit categories in the <Link to="/manchi/studio/vault" className="text-pink-600 underline">Vault</Link>.
          </p>
        </div>
        <Button onClick={generate} disabled={building} data-testid="manchi-daily-generate"
          className="rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold shadow-lg shadow-pink-300/40">
          {building ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {brief ? "Refresh brief" : "Generate today's brief"}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-rose-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : building && !brief ? (
        <div className="rounded-3xl border border-dashed border-pink-200 p-12 text-center text-rose-400">
          <Loader2 className="w-6 h-6 animate-spin inline mb-2" /><br />Curating the day&apos;s major news…
        </div>
      ) : !brief || !(brief.sections || []).length ? (
        <div className="rounded-3xl border border-dashed border-pink-200 p-12 text-center text-rose-400">
          No brief yet today. Tap <span className="text-pink-600 font-semibold">Generate today&apos;s brief</span> to get started.
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 text-xs text-rose-400 mb-4">
            <Globe2 className="w-4 h-4" /> {brief.region} · {brief.date}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {brief.sections.map((s, i) => (
              <div key={i} data-testid={`manchi-brief-section-${i}`}
                className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-2.5 h-2.5 rounded-full ${DOT_COLORS[i % DOT_COLORS.length]}`} />
                  <h3 className="font-black text-rose-900">{s.name}</h3>
                </div>
                <ul className="space-y-2">
                  {s.points.map((p, j) => (
                    <li key={j} className="text-rose-700 text-sm leading-snug flex gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-pink-400 mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
